import CustomNode from '@/components/flow/nodes/CustomNode';
import CustomNodeAlt from '@/components/flow/nodes/CustomNodeAlt';
import { useThemeMode } from '@/hooks/useThemeMode';
import { EdgeTheme, FlowTheme } from '@/types/theme';
import { isNumber } from '@/util/typeGuards';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import HelpIcon from '@mui/icons-material/Help';
import LightModeIcon from '@mui/icons-material/LightMode';
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tooltip
} from '@mui/material';
import React, { FC } from 'react';
import TextFieldWrapper from '../TextFieldWrapper';

const nodePaddingText =
  'How many pixels of padding are added around nodes, or by how much should the edge avoid the walls of a node. Default 10, minimum 2.';
const gridRatioText =
  'The size in pixels of each square grid cell used for path-finding. Smaller values for a more accurate path, bigger for faster path-finding. Default 10, minimum 10.';

const ThemeManager: FC = () => {
  const {
    gridRatio,
    setGridRatio,
    nodePadding,
    setNodePadding,
    flowTheme,
    edgeTheme,
    mode,
    toggleFlowTheme,
    toggleEdgeTheme,
    toggleMode
  } = useThemeMode();

  const nodeSize = { height: '6em', width: '7.5em' };

  const handleNodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setValue((event.target as HTMLInputElement).value);
    const value = event.target.value;
    toggleFlowTheme(value as FlowTheme);
  };

  const handleEdgeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setValue((event.target as HTMLInputElement).value);
    const value = event.target.value;
    toggleEdgeTheme(value as EdgeTheme);
  };

  return (
    <Stack direction='column' p={1} gap={1} alignItems='center'>
      <Stack direction='row' alignItems='center'>
        <DarkModeIcon />
        <Switch checked={mode === 'light'} onChange={() => toggleMode()} inputProps={{ 'aria-label': '' }} />
        <LightModeIcon />
      </Stack>
      <Divider />
      {location.pathname.startsWith('/builder') && (
        <>
          <Stack direction='row' alignItems='center'>
            <FormControl component='fieldset'>
              <FormLabel component='legend'></FormLabel>
              <RadioGroup row value={flowTheme} onChange={handleNodeChange}>
                <FormControlLabel
                  labelPlacement='bottom'
                  value='normal'
                  label={
                    <CustomNode
                      id={''}
                      data={{
                        backendNode: {
                          type: 'Nodes',
                          label: ''
                        },
                        openDetails: undefined,
                        onDetailsClicked: undefined,
                        details: undefined,
                        handles: undefined
                      }}
                      positionAbsoluteX={0}
                      positionAbsoluteY={0}
                      selectable
                      deletable
                      draggable
                      size={nodeSize}
                      selected={false}
                      type={''}
                      zIndex={0}
                      isConnectable={false}
                      dragging={false}
                      blockStyle={undefined}
                      isStartNode={false}
                      sourceHandles={[]}
                    />
                  }
                  control={<Radio />}
                />
                <FormControlLabel
                  labelPlacement='bottom'
                  value='alt'
                  label={
                    <CustomNodeAlt
                      id={''}
                      data={{
                        backendNode: {
                          type: 'Nodes Alt',
                          label: ''
                        },
                        openDetails: undefined,
                        onDetailsClicked: undefined,
                        details: undefined,
                        handles: undefined
                      }}
                      size={nodeSize}
                      selected={false}
                      type={''}
                      zIndex={0}
                      isConnectable={false}
                      positionAbsoluteX={0}
                      positionAbsoluteY={0}
                      selectable
                      deletable
                      draggable
                      dragging={false}
                      blockStyle={undefined}
                      isStartNode={false}
                      sourceHandles={[]}
                    />
                  }
                  control={<Radio />}
                />
              </RadioGroup>
              <FormHelperText></FormHelperText>
            </FormControl>
          </Stack>
          <Divider />
          <RadioGroup row value={edgeTheme} onChange={handleEdgeChange}>
            <FormControlLabel labelPlacement='bottom' value='bezier' label='Bezier' control={<Radio />} />
            <FormControlLabel labelPlacement='bottom' value='straight' label='Straight' control={<Radio />} />
            <FormControlLabel labelPlacement='bottom' value='step' label='Step' control={<Radio />} />
            <FormControlLabel labelPlacement='bottom' defaultChecked value='smart' label='Smart' control={<Radio />} />
          </RadioGroup>
          {edgeTheme === 'smart' && (
            <>
              <Stack direction='row' alignItems='center'>
                <TextFieldWrapper
                  size='small'
                  label='Node padding'
                  value={nodePadding}
                  onChange={(e) => {
                    const newValue = Number(e.currentTarget.value);
                    if (!isNumber(newValue) || newValue < 2) return;
                    setNodePadding(newValue);
                  }}
                  slotProps={{ htmlInput: { min: 2, autoComplete: 'off' } }}
                  sx={{ mr: 1 }}
                />
                <Tooltip title={nodePaddingText} placement='top' arrow>
                  <HelpIcon fontSize='small' />
                </Tooltip>
              </Stack>
              <Stack direction='row' alignItems='center'>
                <TextFieldWrapper
                  size='small'
                  label='Grid ratio'
                  value={gridRatio}
                  onChange={(e) => {
                    const newValue = Number(e.currentTarget.value);
                    if (!isNumber(newValue) || newValue < 10) return;
                    setGridRatio(newValue);
                  }}
                  slotProps={{ htmlInput: { min: 10, autoComplete: 'off' } }}
                  sx={{ mr: 1 }}
                />
                <Tooltip title={gridRatioText} placement='top' arrow>
                  <HelpIcon fontSize='small' />
                </Tooltip>
              </Stack>
            </>
          )}
        </>
      )}
    </Stack>
  );
};

export default ThemeManager;
